import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  ElementRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  Overlay,
  OverlayRef,
  ConnectedPosition,
  OverlayPositionBuilder,
} from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

@Directive({
  selector: '[appTooltipOverlay]',
  standalone: false,
})
export class TooltipOverlayDirective implements OnInit, OnDestroy {
  /**
   * Template da visualizzare
   */
  @Input('appTooltipOverlay') tooltipTemplate!: TemplateRef<any>;

  // Classe css personalizzata per il tooltip. Se non viene passata allora prenderà quella di default
  @Input() tooltipClass?: string;

  /** Quando si definisce un template che rapresenti il tooltip, che poi viene passato
  alla [appTooltipOverlay], si ha dei casi in cui ci serviranno passare dei parametri a questo template, per renderlo dinamico.
  tramite [variabiliTemplate] possiamo passare i parametri necessari a costruire il template, 
  esempio : 

   <span class="material-icons-outlined" [appTooltipOverlay]="tooltipVariabiliTemplate"
          [variabiliTemplate]="{ elementoChk: elementoChk, validato : elementoChk.validato }">
         help_outline
     </span>

  <ng-template #tooltipVariabiliTemplate let-elementoChk="elementoVariabile" let-validato="validato">
    <div>
       <div>{{elementoVariabile}}</div>
       <div>{{validato}}</div>
    </div>
</ng-template>

Come possiamo vedere nel template avevo bisogno di 2 parametri, elementoVariabile e validato. 
   */
  @Input() variabiliTemplate: Record<string, any> = {};
  @Input() position: TooltipPosition = 'bottom';

  private overlayRef: OverlayRef | null = null;
  private hideTimeout: any = null;

  constructor(
    private readonly overlay: Overlay,
    private readonly elementRef: ElementRef,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly positionBuilder: OverlayPositionBuilder,
  ) {}

  ngOnInit() {
    const positions = this.getTooltipPosition(this.position);

    const positionStrategy = this.positionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(positions);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const el = this.elementRef.nativeElement as HTMLElement;

    el.addEventListener('mouseenter', () => {
      this.cancelHide();
      this.show();
    });

    el.addEventListener('mouseleave', () => this.scheduleHide());
  }

  /**
   * Mostra e configura tooltip
   */
  show() {
    if (this.overlayRef && !this.overlayRef.hasAttached()) {
      const tooltipPortal = new TemplatePortal(
        this.tooltipTemplate,
        this.viewContainerRef,
        this.variabiliTemplate,
      );

      const viewRef = this.overlayRef.attach(tooltipPortal);
      const rootNode = viewRef?.rootNodes?.[0] as HTMLElement | undefined;

      if (rootNode instanceof HTMLElement && rootNode.style) {
        if (this.tooltipClass) {
          rootNode.classList.add(this.tooltipClass);
        } else {
          Object.assign(rootNode.style, {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '800px',
            minWidth: 'auto',
            maxHeight: '800px',
            overflow: 'auto',
            padding: '15px',
            border: '1px solid #ccc',
            borderRadius: '5px',
            backgroundColor: '#fff',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            wordWrap: 'break-word',
            fontSize: '0.9rem',
            zIndex: '1000',
            margin: '15px',
            fontFamily: 'Sora',
          });
        }

        rootNode.addEventListener('mouseenter', () => this.cancelHide());
        rootNode.addEventListener('mouseleave', () => this.scheduleHide());
      } else {
        console.warn('Tooltip rootNode non trovato o non valido:', viewRef?.rootNodes);
      }
    }
  }

  /**
   * Nascondi tooltip
   */
  hide() {
    this.overlayRef?.detach();
  }

  scheduleHide() {
    this.hideTimeout = setTimeout(() => this.hide(), 200);
  }

  cancelHide() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  ngOnDestroy() {
    this.overlayRef?.dispose();
  }

  /**
   * Definizione di posizione del tooltip
   * @param position
   * @returns
   */
  private tooltipPosition(position: TooltipPosition): ConnectedPosition[] {
    switch (position) {
      case 'top':
        return [
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
          },
        ];
      case 'bottom':
        return [
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
          },
        ];
      case 'left':
        return [
          {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
          },
        ];
      case 'right':
        return [
          {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
          },
        ];
      default:
        throw new Error(`Posizione non valida: ${position}`);
    }
  }

  /**
   * Get tooltip posision
   * @param position
   * @returns
   */
  private getTooltipPosition(position?: TooltipPosition): ConnectedPosition[] {
    return this.tooltipPosition(position ?? 'bottom');
  }
}
